import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth, useNotification } from './index';
import { hasuraClient } from '../utils/graphqlClient';
import { mergeWhereConditions } from '../utils/buildHasuraWhere';

const useDynamicEntity = config => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const { user } = useAuth();

  const {
    entityType,
    gqlEntityKey,
    queryName,
    createMutation,
    updateMutation,
    deleteMutation,
    transformData = data => data,
    transformFormData = formData => formData,
    customWhereCondition,
    customDepsKey,
    // Mutation pattern configuration
    mutationPattern = 'default', // 'default', 'companies', 'users', 'teams', 'groups', 'custom'
    createVariableName = 'object', // 'object', 'data', 'input', 'name', etc.
    updateVariableName = '_set', // '_set', 'data', 'input'
    // For direct variable mutations (teams, groups)
    createDirectVariables = false, // true for teams, groups
    updateDirectVariables = false, // true for teams, groups
    // ---- Backend pagination (Task 3) ----
    // When true, the hook passes `limit`/`offset` to the GraphQL query and
    // tracks page/pageSize/totalCount state. When false (default), fetches
    // everything and leaves pagination to the client-side DataTable.
    serverPagination = false,
    // Name of the _aggregate field on the query result (e.g. 'offers_aggregate')
    // used to read the exact total count. If omitted, the hook falls back to a
    // `limit+1` fetch strategy — it loads pageSize+1 rows, displays the first
    // pageSize, and uses the extra row's presence to know if there's a next
    // page. See CLAUDE.md note: _aggregate is disabled for most tables.
    aggregateKey = null,
    // Default page size for server pagination.
    defaultPageSize = 20,
    // Hasura permission-locked kolonlar. Update mode'da _set payload'undan
    // cikarilir — team_member kendi user_id'sini update etmemeli gibi.
    // Ornek: userRevenueEntries -> updateStripFields: ['user_id'].
    updateStripFields = [],
  } = config;

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Backend pagination state
  const [page, setPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState(defaultPageSize);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);

  // Extra Hasura `_bool_exp` injected from the page (DataTable filters/search).
  // Merged with the permission-driven `customWhereCondition` at fetch time so
  // backend filtering covers ALL pages (not just the loaded slice).
  const [extraWhereCondition, setExtraWhereConditionState] = useState(null);
  // Stable JSON representation used for dep tracking. Lets the page reset
  // effect react when the *contents* change without depending on identity.
  const extraWhereDepsKey = useMemo(
    () => (extraWhereCondition ? JSON.stringify(extraWhereCondition) : ''),
    [extraWhereCondition]
  );

  const setExtraWhereCondition = useCallback(next => {
    setExtraWhereConditionState(prev => {
      const prevKey = prev ? JSON.stringify(prev) : '';
      const nextKey = next ? JSON.stringify(next) : '';
      if (prevKey === nextKey) return prev;
      return next;
    });
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);

      let permissionWhere = {};

      if (customWhereCondition) {
        permissionWhere = customWhereCondition;
      } else if (user?.role !== 'ROOT' && user?.companyId) {
        permissionWhere = { company_id: { _eq: user.companyId } };
      }

      const whereCondition = mergeWhereConditions(
        permissionWhere,
        extraWhereCondition
      );

      const { [queryName]: query } = await import('../graphql');

      const queryVariables = {};
      if (whereCondition && Object.keys(whereCondition).length > 0) {
        queryVariables.where = whereCondition;
      }

      if (serverPagination) {
        queryVariables.offset = (page - 1) * pageSize;
        // When no aggregate is available, fetch one extra row to detect if
        // there is a next page. We display only `pageSize` rows but know
        // from `rawData.length > pageSize` that more exist.
        queryVariables.limit = aggregateKey ? pageSize : pageSize + 1;
      }

      const result = await hasuraClient.query(query, queryVariables);

      const rawData = result[gqlEntityKey] || [];

      if (serverPagination) {
        if (aggregateKey && result[aggregateKey]?.aggregate?.count != null) {
          const exactCount = result[aggregateKey].aggregate.count;
          setTotalCount(exactCount);
          setHasNextPage(page * pageSize < exactCount);
          const transformedData = rawData.map(transformData);
          setData(transformedData);
        } else {
          // limit+1 strategy: drop the probe row and approximate total so the
          // pagination UI can still render first/prev/next/last sensibly.
          const hasMore = rawData.length > pageSize;
          const visibleRaw = hasMore ? rawData.slice(0, pageSize) : rawData;
          const transformedData = visibleRaw.map(transformData);
          setData(transformedData);
          setHasNextPage(hasMore);
          // Approximate total: at least what we've seen so far. If there's a
          // next page we add one extra "virtual" row so the pagination
          // component shows a next-page button.
          const seen = (page - 1) * pageSize + visibleRaw.length;
          setTotalCount(hasMore ? seen + 1 : seen);
        }
      } else {
        const transformedData = rawData.map(transformData);
        setData(transformedData);
        setTotalCount(transformedData.length);
        setHasNextPage(false);
      }
    } catch (error) {
      showNotification(t(`hooks.${entityType}.errors.fetchList`), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [
    user?.role,
    user?.companyId,
    customWhereCondition,
    extraWhereCondition,
    queryName,
    gqlEntityKey,
    transformData,
    entityType,
    showNotification,
    t,
    serverPagination,
    aggregateKey,
    page,
    pageSize,
  ]);

  const triggerRefresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const handleFormSubmit = useCallback(
    async (formData, entityId = null) => {
      try {
        const companyId =
          mutationPattern === 'companies'
            ? null
            : formData.companyId || user?.companyId || 1;
        const transformedFormData = transformFormData(formData);

        if (entityId) {
          setIsUpdating(true);

          const { [updateMutation]: mutation } = await import('../graphql');

          // Determine update variables based on pattern
          let updateVariables = {};

          if (mutationPattern === 'companies') {
            updateVariables = {
              id: parseInt(entityId),
              data: transformedFormData,
            };
          } else if (mutationPattern === 'jobs') {
            updateVariables = {
              id: entityId,
              job: transformedFormData,
            };
          } else if (mutationPattern === 'users') {
            updateVariables = {
              id: parseInt(entityId),
              input: transformedFormData,
            };
          } else if (mutationPattern === 'teams') {
            // For teams, include company_id if user is ROOT
            const updateData = { ...transformedFormData };
            // Only allow company_id update for ROOT users
            if (user?.role !== 'ROOT' && updateData.company_id) {
              delete updateData.company_id;
            }

            updateVariables = {
              id: parseInt(entityId),
              ...updateData,
            };
          } else if (updateDirectVariables) {
            // For direct variable mutations (teams, groups)
            const updateData = { ...transformedFormData };
            
            // Only allow company_id update for ROOT users
            if (user?.role !== 'ROOT' && updateData.company_id) {
              delete updateData.company_id;
            }

            updateVariables = {
              id: parseInt(entityId),
              ...updateData,
            };
          } else {
            // updateStripFields listesindeki kolonlari _set'ten cikar
            // (Hasura permission update columns'a izinli olmayan alanlar).
            const setPayload = updateStripFields.length > 0
              ? Object.fromEntries(
                  Object.entries(transformedFormData).filter(
                    ([key]) => !updateStripFields.includes(key),
                  ),
                )
              : transformedFormData;
            updateVariables = {
              id: parseInt(entityId),
              [updateVariableName]: setPayload,
            };
          }

          const result = await hasuraClient.mutate(mutation, updateVariables);

          const updateKey = `update_${gqlEntityKey}_by_pk`;
          if (result[updateKey]) {
            showNotification(
              t(`hooks.${entityType}.success.updated`),
              'success'
            );

            if (config.onSuccess) {
              try {
                await config.onSuccess('update', result[updateKey], formData);
              } catch (error) {
                console.error('Error in onSuccess callback:', error);
              }
            }

            triggerRefresh();
            return { success: true };
          }
        } else {
          setIsCreating(true);

          const { [createMutation]: mutation } = await import('../graphql');

          // For companies, don't add company_id field
          const createData =
            mutationPattern === 'companies'
              ? transformedFormData
              : {
                  ...transformedFormData,
                  company_id: companyId,
                };

          // Determine create variables based on pattern
          let createVariables = {};

          if (mutationPattern === 'companies') {
            createVariables = {
              data: createData,
            };
          } else if (mutationPattern === 'jobs') {
            createVariables = {
              job: createData,
            };
          } else if (mutationPattern === 'users') {
            createVariables = {
              input: createData,
            };
          } else if (mutationPattern === 'teams') {
            createVariables = createData;
          } else if (createDirectVariables) {
            // For direct variable mutations (teams, groups)
            createVariables = createData;
          } else {
            createVariables = {
              [createVariableName]: createData,
            };
          }

          const result = await hasuraClient.mutate(mutation, createVariables);

          const insertKey = `insert_${gqlEntityKey}_one`;
          if (result[insertKey]) {
            showNotification(
              t(`hooks.${entityType}.success.created`),
              'success'
            );

            if (config.onSuccess) {
              try {
                await config.onSuccess('create', result[insertKey], formData);
              } catch (error) {
                console.error('Error in onSuccess callback:', error);
              }
            }

            triggerRefresh();
            return { success: true };
          }
        }

        throw new Error('No data returned from server');
      } catch (error) {
        // Hasura GraphQL hatalarını console'a tam bas — sessiz kalmasın.
        // Defansif: response'un farklı şekillerini deniyoruz (errors[0].message
        // sıklıkla anlamlı kök sebep — örn. CHECK constraint trip, NOT NULL ihlali).
        const hasuraMsg =
          error?.response?.errors?.[0]?.message ||
          error?.errors?.[0]?.message ||
          error?.message;
        console.error(`[${entityId ? 'update' : 'create'} ${entityType}] failed:`, hasuraMsg, error);
        const baseMessage = entityId
          ? t(`hooks.${entityType}.errors.updateError`)
          : t(`hooks.${entityType}.errors.createError`);
        showNotification(
          hasuraMsg ? `${baseMessage}: ${hasuraMsg}` : baseMessage,
          'error',
        );
        return { success: false, error };
      } finally {
        setIsCreating(false);
        setIsUpdating(false);
      }
    },
    [
      user?.companyId,
      entityType,
      gqlEntityKey,
      createMutation,
      updateMutation,
      transformFormData,
      mutationPattern,
      createVariableName,
      updateVariableName,
      createDirectVariables,
      updateDirectVariables,
      showNotification,
      t,
      triggerRefresh,
    ]
  );

  const deleteEntity = useCallback(
    async entityId => {
      try {
        setIsDeleting(true);

        // For jobs, entityId is already a UUID string, don't parse as integer
        let idToUse;
        if (mutationPattern === 'jobs') {
          idToUse = entityId; // Keep as string for UUID
        } else {
          const numericId = parseInt(entityId);
          if (!entityId || isNaN(numericId)) {
            throw new Error(`Invalid ${entityType} ID: ${entityId}`);
          }
          idToUse = numericId;
        }

        const { [deleteMutation]: mutation } = await import('../graphql');

        const result = await hasuraClient.mutate(mutation, {
          id: idToUse,
        });

        const deleteKey = `delete_${gqlEntityKey}_by_pk`;
        if (result[deleteKey]) {
          showNotification(t(`hooks.${entityType}.success.deleted`), 'success');
          triggerRefresh();
          return { success: true };
        }

        throw new Error('No data returned from server');
      } catch (error) {
        console.error(`Error deleting ${entityType}:`, error);
        showNotification(t(`hooks.${entityType}.errors.deleteError`), 'error');
        return { success: false, error };
      } finally {
        setIsDeleting(false);
      }
    },
    [
      entityType,
      gqlEntityKey,
      deleteMutation,
      showNotification,
      t,
      triggerRefresh,
    ]
  );

  // Reset to page 1 whenever the where condition CONTENTS change — stale
  // offsets on a different filter set would yield empty pages. We track the
  // previous signature in a ref so the effect skips the initial mount and
  // only resets when the user actually changes filters/permissions, not on
  // every render that produces a new `customWhereCondition` reference.
  const resetSigRef = useRef(null);
  useEffect(() => {
    if (!serverPagination) return;
    const sig = `${customDepsKey || ''}|${extraWhereDepsKey}`;
    if (resetSigRef.current === null) {
      resetSigRef.current = sig;
      return;
    }
    if (resetSigRef.current !== sig) {
      resetSigRef.current = sig;
      setPageState(1);
    }
  }, [serverPagination, customDepsKey, extraWhereDepsKey]);

  useEffect(() => {
    if (user?.id) {
      fetchData();
    }
    // When serverPagination is on, fetchData's deps already include page and
    // pageSize, so re-running on their change is handled via fetchData identity.
  }, [user?.id, user?.role, user?.companyId, refreshTrigger, customDepsKey, extraWhereDepsKey, serverPagination ? page : null, serverPagination ? pageSize : null]);

  const setPage = useCallback((newPage) => {
    setPageState(Math.max(1, newPage));
  }, []);

  const setPageSize = useCallback((newSize) => {
    setPageSizeState(newSize);
    // Jump back to the first page so the user doesn't land on an empty page
    // when shrinking the dataset window.
    setPageState(1);
  }, []);

  return {
    [entityType]: Array.isArray(data) ? data : [],
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    handleFormSubmit,
    [`delete${entityType.charAt(0).toUpperCase() + entityType.slice(1)}`]:
      deleteEntity,
    fetchData,
    // Fetch the ENTIRE dataset honoring the current permission + user filter
    // where conditions, but bypassing pagination. Used by CSV export so the
    // user gets every row, not just the visible page.
    fetchAllForExport: async () => {
      try {
        let permissionWhere = {};
        if (customWhereCondition) {
          permissionWhere = customWhereCondition;
        } else if (user?.role !== 'ROOT' && user?.companyId) {
          permissionWhere = { company_id: { _eq: user.companyId } };
        }
        const whereCondition = mergeWhereConditions(
          permissionWhere,
          extraWhereCondition,
        );
        const { [queryName]: query } = await import('../graphql');
        const queryVariables = {};
        if (whereCondition && Object.keys(whereCondition).length > 0) {
          queryVariables.where = whereCondition;
        }
        // Explicit large limit acts as a safety cap; offset omitted.
        queryVariables.limit = 100000;
        queryVariables.offset = 0;
        const result = await hasuraClient.query(query, queryVariables);
        const rawData = result[gqlEntityKey] || [];
        return rawData.map(transformData);
      } catch (err) {
        console.error('fetchAllForExport failed:', err);
        return [];
      }
    },
    triggerRefresh,
    refreshTrigger,

    // Backend pagination (only meaningful when serverPagination: true)
    serverPagination,
    page,
    pageSize,
    totalCount,
    hasNextPage,
    setPage,
    setPageSize,

    // Backend filter/search (only meaningful when serverPagination: true).
    // Pages compute a Hasura `_bool_exp` from DataTable filter/search state
    // via `buildHasuraWhere(...)` and push it through this setter. The hook
    // merges it with `customWhereCondition` (permissions) on every fetch.
    extraWhereCondition,
    setExtraWhereCondition,

    // Additional data support
    additionalData: {},
  };
};

export { useDynamicEntity };
